import * as THREE from 'three';
import gsap from 'gsap';

function mediaMatches(query: string): boolean {
  return typeof matchMedia === 'function' && matchMedia(query).matches;
}

export const REDUCED = mediaMatches('(prefers-reduced-motion: reduce)');
export const COARSE = mediaMatches('(pointer: coarse)');

/** Ink shader hover — desktop fine pointer only; mobile/reduced-motion use scale/tilt. */
export const INK_HOVER = !REDUCED && !COARSE;

const HOVER_Z = 0.025;
type ArtMesh = THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;

interface InkState {
  amount: { value: number };
  tint: THREE.Color;
  time: { value: number };
  amountTween?: gsap.core.Tween;
  timeTween?: gsap.core.Tween;
}

const states = new WeakMap<THREE.MeshBasicMaterial, InkState>();

function patchInkShader(mat: THREE.MeshBasicMaterial, tintHex: string): InkState {
  let state = states.get(mat);
  if (!state) {
    state = {
      amount: { value: 0 },
      tint: new THREE.Color(tintHex),
      time: { value: 0 },
    };
    states.set(mat, state);
  } else {
    state.tint.set(tintHex);
  }

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uInkAmount = state!.amount;
    shader.uniforms.uInkTint = { value: state!.tint };
    shader.uniforms.uInkTime = state!.time;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vInkUv;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvInkUv = uv;');

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uInkAmount;
uniform vec3 uInkTint;
uniform float uInkTime;
varying vec2 vInkUv;`,
      )
      .replace(
        '#include <map_fragment>',
        `{
  vec2 c = vInkUv - 0.5;
  float r = length(c);
  float wave = sin(r * 14.0 - uInkTime * 3.0) * (1.0 - smoothstep(0.0, 0.55, r));
  vec2 warp = c * wave * uInkAmount * 0.04;
  #ifdef USE_MAP
    vec4 inkTex = texture2D(map, vInkUv + warp);
    diffuseColor *= inkTex;
  #endif
  float bleed = uInkAmount * (0.12 + 0.18 * (1.0 - smoothstep(0.0, 0.7, r)));
  diffuseColor.rgb = mix(diffuseColor.rgb, uInkTint, bleed);
}`,
      );
  };
  mat.customProgramCacheKey = () => 'ink-hover-v1';

  return state;
}

export function applyInkHover(mesh: ArtMesh, tintHex: string): void {
  if (!INK_HOVER) return;

  const mat = mesh.material;
  const state = patchInkShader(mat, tintHex);
  mat.needsUpdate = true;

  state.amountTween?.kill();
  state.timeTween?.kill();
  gsap.killTweensOf(mesh.position);

  state.amountTween = gsap.to(state.amount, {
    value: 1,
    duration: 0.45,
    ease: 'power2.out',
  });
  state.timeTween = gsap.to(state.time, {
    value: 8,
    duration: 3,
    ease: 'none',
    repeat: -1,
  });
  const restZ = (mesh.userData.restZ as number) ?? 0;
  gsap.to(mesh.position, {
    z: restZ + HOVER_Z,
    duration: 0.45,
    ease: 'power2.out',
  });
}

export function clearInkHover(mesh: ArtMesh): void {
  if (!INK_HOVER) return;

  const state = states.get(mesh.material);
  if (!state) return;

  state.amountTween?.kill();
  state.timeTween?.kill();
  gsap.killTweensOf(mesh.position);

  state.amountTween = gsap.to(state.amount, {
    value: 0,
    duration: 0.35,
    ease: 'power2.inOut',
  });
  const restZ = (mesh.userData.restZ as number) ?? 0;
  gsap.to(mesh.position, {
    z: restZ,
    duration: 0.35,
    ease: 'power2.inOut',
  });
}
